import { ClickOffer } from "@lib/shared/types";
import { PageProps } from "@services/initialCallsTypes";
import { useRouter } from "next/router";
import {
    createContext,
    Dispatch,
    ReactElement,
    ReactNode,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { DomainForm } from "src/api";
import { useTimeOnSite } from "./useTimeOnSite";
import { updateDomainOffers } from "src/api/domainInfo";
import { getUserState } from "@lib/shared/cookies";
import { getDomainNameWithoutExtension } from "@lib/sharedUtils";
import { getTextBlocks } from "src/api/getTextBlocks";
import { AngiWidgetDetails } from "@components/shared/thankyou/widgets/angiWidget/angiWidget";
import { getTrustedFormCertificateUrl } from "@lib/shared/certificateUtils";
import { sendTrackingTime as sendVisitMeta } from "src/api/trackingTime";
type DomainContextProps = Pick<
    PageProps,
    | "visitDetails"
    | "zipCodeLocation"
    | "domain"
    | "domainPopup"
    | "comparisonListing"
    | "formattedTitle"
    | "categoryListing"
    | "category"
    | "clicksOffers"
    | "zipCode"
    | "form"
    | "locale"
    | "defaultLocale"
    | "locales"
    | "content"
    | "userAgent"
    | "isBot"
    | "formTemplate"
    | "formOffer"
    | "exitModalOffer"
    | "thankYouOffers"
    | "headerOffer"
    | "thankyouListing"
    | "secondListing"
    | "stickyOffer"
    | "testConfig"
    | "blogPost"
    | "autoWarrantyMakers"
    | "totalTimeSpendOnSite"
    | "routeConfig"
    | "externalFavicon"
    | "pageBuilderBlogPost"
    | "pageBuilderLookupLists"
    | "pageBuilderListings"
    | "piiCompletionData"
    | "redirectTo"
    | "pageType"
    | "doNotSellFormStates"
    | "disableTimeOnSite"
    | "showFormModal"
    | "loadOneToOneForm"
    | "ignoreSticky"
    | "nearbyZipCodes"
    | "menus"
    | "domainPosts"
    | "referrer"
> & {
    updateClicksOffers: Dispatch<
        SetStateAction<ClickOffer[] | null | undefined>
    >;
    updateForm: Dispatch<SetStateAction<DomainForm | null | undefined>>;
    selectedCountryCode: string | null;
    updateSelectedCountryCode: Dispatch<SetStateAction<string>>;
    handleUpdateOffers: (state: string) => void;
    loadingOffers: boolean;
    buyerPhoneNumber: string | null;
    setBuyerPhoneNumber: (phoneNumber: string | null) => void;
    apiCallLoading: boolean;
    setApiCallLoading: (loading: boolean) => void;
    domainNameTranslation: string;
    setAngiWidgetDetails: (details: AngiWidgetDetails) => void;
    angiWidgetDetails: AngiWidgetDetails;
};

const DomainContext = createContext<DomainContextProps | undefined>(undefined);

export const DomainProvider = (
    props: PageProps & { children: ReactNode },
): ReactElement => {
    const {
        visitDetails,
        referrer,
        domain: initialDomain,
        formattedTitle,
        domainPopup,
        categoryListing,
        comparisonListing,
        zipCodeLocation,
        category,
        clicksOffers: offers,
        zipCode,
        form: domainForm,
        locale,
        locales,
        defaultLocale,
        content,
        userAgent,
        isBot,
        formTemplate,
        formOffer: initialFormOffer,
        headerOffer: initialHeaderOffer,
        stickyOffer: initialStickyOffer,
        exitModalOffer: initialExitModalOffer,
        thankYouOffers: initialThankYouOffers,
        thankyouListing,
        secondListing,
        testConfig,
        blogPost,
        initialTimeOnSite,
        routeConfig,
        externalFavicon,
        pageBuilderBlogPost,
        pageBuilderLookupLists,
        pageBuilderListings,
        autoWarrantyMakers,
        piiCompletionData,
        redirectTo,
        disableTimeOnSite,
        pageType,
        doNotSellFormStates,
        ignoreSticky,
        nearbyZipCodes,
        menus,
        domainPosts,
    } = props;

    const [userSelectedCountryCode, updateSelectedCountryCode] = useState(
        visitDetails?.countryCode,
    );

    const { query, asPath } = useRouter();
    const selectedCountryCode = useMemo(() => {
        if (
            !domainForm?.supportedCountries ||
            !domainForm?.supportedCountries?.length ||
            userSelectedCountryCode === "US"
        ) {
            return null;
        }

        if (query?.countryCode) {
            return query?.countryCode as string;
        }

        return userSelectedCountryCode;
    }, [query?.countryCode, userSelectedCountryCode]);

    const [clicksOffers, updateClicksOffers] = useState(offers);
    const [form, updateForm] = useState(domainForm);
    const { totalTimeSpendOnSite } = useTimeOnSite(
        initialTimeOnSite ?? 0,
        disableTimeOnSite,
    );

    const [trustedFormCertificateUrl, setTrustedFormCertificateUrl] = useState<
        string | undefined
    >(undefined);
    useEffect(() => {
        const fetchTrustedFormUrl = async () => {
            const url = await getTrustedFormCertificateUrl();
            if (url) setTrustedFormCertificateUrl(url);
        };
        void fetchTrustedFormUrl();
    }, []);

    useEffect(() => {
        if (!trustedFormCertificateUrl) return;
        void sendVisitMeta({
            event: "trustedFormCertificate",
            eventValue: trustedFormCertificateUrl,
            eventCategory: "meta",
        });
    }, [trustedFormCertificateUrl]);

    const [domainOffers, setDomainOffers] = useState({
        formOffer: initialFormOffer,
        headerOffer: initialHeaderOffer,
        stickyOffer: initialStickyOffer,
        exitModalOffer: initialExitModalOffer,
        thankYouOffers: initialThankYouOffers,
    });
    const [domain, setDomain] = useState(initialDomain);
    const domainNameTranslation = getDomainNameWithoutExtension(
        domain?.name || "",
    );
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [buyerPhoneNumber, setBuyerPhoneNumber] = useState<string | null>(
        null,
    );
    const [apiCallLoading, setApiCallLoading] = useState(false);
    const [angiWidgetDetails, setAngiWidgetDetails] =
        useState<AngiWidgetDetails>({
            showWidget: undefined,
            cjLeadKey: "",
        });

    // Save referrer to session storage for persistence across page refreshes
    useEffect(() => {
        if (referrer && typeof window !== "undefined") {
            sessionStorage.setItem("page_referrer", String(referrer));
        }
    }, [referrer]);

    // Load text blocks when browser is idle
    // Skip if we're on legal pages (already fetched on server)
    useEffect(() => {
        const isLegalPage =
            asPath?.includes("/privacy-policy") ||
            asPath?.includes("/terms-and-conditions") ||
            asPath?.includes("/ccpa-privacy-notice");

        // Skip fetching if already on a legal page (server-side fetched)
        if (isLegalPage) {
            return;
        }

        const fetchTextBlocks = async () => {
            try {
                const { data } = await getTextBlocks({
                    categorySlug: category?.slug,
                    positions: [
                        "custom-domain-privacy-policy",
                        "custom-category-privacy-policy",
                        "custom-domain-terms-and-conditions",
                        "custom-category-terms-and-conditions",
                        "custom-domain-ccpa-privacy-notice",
                        "custom-category-ccpa-privacy-notice",
                    ],
                });
                if (data && data.data.length > 0) {
                    setDomain((prevDomain) => ({
                        ...prevDomain,
                        textBlocks: [
                            ...(prevDomain?.textBlocks ?? []),
                            ...data.data,
                        ],
                    }));
                }
            } catch (error) {
                // failed
            }
        };
        void fetchTextBlocks();
    }, [asPath]);

    const loadOneToOneForm =
        (query.oto === "true" || category?.isOneToOneConsent) &&
        query.oto !== "false";

    const updateOffers = async (stateCode: string) => {
        setLoadingOffers(true);
        const { data } = await updateDomainOffers({
            categorySlug: category?.slug,
            stateCode: stateCode,
            isMobile: visitDetails.isMobile,
            query,
        });
        setLoadingOffers(false);

        if (data) {
            const {
                formOffer: updatedFormOffer,
                exitModalOffer: updatedExitModalOffer,
                thankYouOffers: updatedThankYouOffers,
                headerOffer: updatedHeaderOffer,
                stickyOffer: updatedStickyOffer,
                defaultOffers,
            } = data;

            setDomain({
                ...initialDomain,
                defaultOffers,
            });

            setDomainOffers({
                formOffer: updatedFormOffer ?? [],
                exitModalOffer: updatedExitModalOffer ?? [],
                thankYouOffers: updatedThankYouOffers ?? [],
                headerOffer: updatedHeaderOffer ?? [],
                stickyOffer: updatedStickyOffer ?? [],
            });
        }
    };

    const handleUpdateOffers = (state: string) => {
        const stateCookie = getUserState();
        if (
            state.toLowerCase() ===
            (stateCookie
                ? String(stateCookie)?.toLowerCase()
                : visitDetails.stateCode?.toLowerCase())
        ) {
            return;
        }
        void updateOffers(state);
    };

    const contextValue = useMemo(
        () => ({
            visitDetails,
            domain,
            formattedTitle,
            domainPopup,
            comparisonListing,
            categoryListing,
            zipCodeLocation,
            category,
            clicksOffers,
            zipCode,
            updateClicksOffers,
            form,
            updateForm,
            locale,
            defaultLocale,
            locales,
            content,
            userAgent,
            isBot,
            formTemplate,
            formOffer: domainOffers.formOffer,
            headerOffer: domainOffers.headerOffer,
            stickyOffer: domainOffers.stickyOffer,
            exitModalOffer: domainOffers.exitModalOffer,
            thankYouOffers: domainOffers.thankYouOffers,
            thankyouListing,
            secondListing,
            testConfig,
            blogPost,
            selectedCountryCode,
            updateSelectedCountryCode,
            totalTimeSpendOnSite: totalTimeSpendOnSite,
            routeConfig,
            externalFavicon,
            pageBuilderBlogPost,
            autoWarrantyMakers,
            pageBuilderLookupLists,
            pageBuilderListings,
            piiCompletionData,
            redirectTo,
            pageType,
            doNotSellFormStates,
            loadOneToOneForm,
            handleUpdateOffers,
            loadingOffers,
            ignoreSticky,
            nearbyZipCodes,
            buyerPhoneNumber,
            setBuyerPhoneNumber,
            apiCallLoading,
            setApiCallLoading,
            domainNameTranslation,
            angiWidgetDetails,
            setAngiWidgetDetails,
            menus,
            domainPosts,
            referrer,
        }),
        [
            visitDetails,
            domain,
            formattedTitle,
            domainPopup,
            comparisonListing,
            categoryListing,
            zipCodeLocation,
            category,
            clicksOffers,
            zipCode,
            updateClicksOffers,
            form,
            updateForm,
            locale,
            defaultLocale,
            locales,
            content,
            userAgent,
            isBot,
            formTemplate,
            domainOffers.formOffer,
            domainOffers.headerOffer,
            domainOffers.stickyOffer,
            domainOffers.exitModalOffer,
            domainOffers.thankYouOffers,
            thankyouListing,
            secondListing,
            testConfig,
            blogPost,
            selectedCountryCode,
            updateSelectedCountryCode,
            totalTimeSpendOnSite,
            routeConfig,
            externalFavicon,
            pageBuilderBlogPost,
            autoWarrantyMakers,
            pageBuilderLookupLists,
            pageBuilderListings,
            piiCompletionData,
            redirectTo,
            pageType,
            doNotSellFormStates,
            loadOneToOneForm,
            handleUpdateOffers,
            loadingOffers,
            ignoreSticky,
            nearbyZipCodes,
            buyerPhoneNumber,
            setBuyerPhoneNumber,
            apiCallLoading,
            setApiCallLoading,
            domainNameTranslation,
            angiWidgetDetails,
            setAngiWidgetDetails,
            menus,
            domainPosts,
            referrer,
        ],
    );

    return (
        <DomainContext.Provider value={contextValue}>
            {props.children}
        </DomainContext.Provider>
    );
};

export const useDomainContext = () => {
    const context = useContext(DomainContext);
    if (context === undefined) {
        throw new Error(
            "useDomainContext must be used within a DomainProvider",
        );
    }
    return context;
};
